import { DataTable, DataTableSelectionMultipleChangeEvent, DataTableStateEvent } from "primereact/datatable";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { useCallback, useMemo, useState } from "react";
import { setPage, setFilters, updateItem, setFailed, setToDelete } from "../../store/slices/positiveItemSlice";
import { cloneDeep } from "lodash";
import { selectPositiveItem } from "../../store/selectors/positiveItemSelectors";
import { PositiveItemTableHeader } from "./PositiveItemTableHeader";
import { defaultPositiveItemColumns } from "../../utils/positiveItemTableHelper";
import { ColumnEvent } from "primereact/column";
import { updatePositiveItem } from "../../services/positiveItemService";
import { selectInspectionIsDisabled } from "../../store/selectors/inspectionSelectors";
import { PositiveItem } from "../../types/positiveItem";

export const PositiveItemTable = () => {
    const dispatch = useAppDispatch();

    const {
        data,
        loading,
        globalFilterValue,
        filters,
        page,
    } = useAppSelector(selectPositiveItem);
    const inspectionDisabled = useAppSelector(selectInspectionIsDisabled);
    const isRowSelectable = () => (!inspectionDisabled);

    const [checkbox, setCheckbox] = useState<PositiveItem[]>([]);

    const onPage = useCallback((event: DataTableStateEvent) => {
        dispatch(setPage({ row: event.rows, first: event.first}))
    }, []);

    const onFilter = useCallback((event: DataTableStateEvent) => {
        dispatch(setFilters(event.filters));
    }, []);

    const toggleSelection = useCallback((event: DataTableSelectionMultipleChangeEvent<PositiveItem[]>) => {
        setCheckbox(event.value);
        dispatch(setToDelete(event.value));
    }, []);

    const onCellEditComplete = useCallback(async (event: ColumnEvent) => {
        const { newValue, newRowData, value } = event;

        if (newValue !== value) {
            dispatch(updateItem(newRowData));

            const updated = await updatePositiveItem(newRowData);
    
            if (!updated) {
                dispatch(setFailed([newRowData.id]));
            }
        }
    }, []);

    const renderHeader = useMemo(() => (
        <PositiveItemTableHeader />
    ), [globalFilterValue]);

    const value = useMemo(() => (
        cloneDeep(data)
    ), [data]);

    const columns = useMemo(() => (
        defaultPositiveItemColumns(onCellEditComplete, inspectionDisabled)
    ), [data]);

    const rowOptions = useMemo(() => [50, 75, 100, 150, 250], []);
    
    return (
        <DataTable 
            value={value}
            dataKey="id"
            loading={loading}
            filters={filters}
            onFilter={onFilter}
            filterDisplay="row"
            editMode="cell"
            paginator
            scrollable
            removableSort
            stripedRows
            size="small"
            first={page.first}
            rows={page.row}
            rowsPerPageOptions={rowOptions}
            onPage={onPage}
            header={renderHeader}
            selectionMode="checkbox"
            selection={checkbox}
            onSelectionChange={toggleSelection}
            isDataSelectable={isRowSelectable}
        >
            {columns}
        </DataTable>
    )
}